import { createRouter as createVueRouter, createWebHashHistory } from 'vue-router'
import { routes, isStepRoute } from '@/steps-meta/routes'
import { initMultiInstanceWarning } from '@/util/multi-instance-warning'
import { delay, poll } from '@/util/helpers'

export function createRouter() {
  const router = createVueRouter({
    history: createWebHashHistory(),
    routes: routes,
    async scrollBehavior(to, from, savedPosition) {
      const scrollOffset = 30

      // Use saved position before anything else
      if (savedPosition) return savedPosition

      // Target not a step? No need to scroll up
      if (!isStepRoute(to)) return

      // Target is a step, but coming from a substep? No need to scroll up
      if (from.matched.includes(to.matched[0])) return

      // Wait for route transition to fade out before scrolling
      await delay(150)

      const container = document.querySelector('.ownbit-configurator') as HTMLElement

      // If the target has a query parameter to jump to an element, scroll to it
      if (to.query.jump) {
        // Wait for the element to appear
        let target: HTMLElement | undefined
        await poll(
          () => {
            const queriedTarget = container.querySelector(
              '#' + String(to.query.jump),
            ) as HTMLElement

            if (queriedTarget) {
              target = queriedTarget
            }

            return queriedTarget !== null
          },
          50,
          1500,
        ).catch(() => {})

        if (target) {
          const headerHeight =
            target.closest('.site-layout')?.querySelector('.header')?.getBoundingClientRect()
              .height ?? 0

          // Animate jump target
          target.animate(
            [
              {
                outline: '4px dotted #004C59',
                outlineOffset:
                  Math.max(Math.min(Math.round(window.innerWidth / 75), 25), 10) + 'px',
              },
              {
                outline: '0px dotted #ffd2ac',
              },
            ],
            {
              delay: 2_500,
              duration: 200,
              fill: 'backwards',
            },
          )

          return {
            top: target.offsetTop - headerHeight - scrollOffset,
            behavior: 'instant',
          }
        }
      }

      return {
        top: container.offsetTop - scrollOffset,
        behavior: 'instant',
      }
    },
  })

  // If next step requires validation, check if current step is valid
  router.beforeEach((to, from) => {
    const stepsStore = useStepsStore()
    const lifecycleStore = useLifecycleStore()

    if (isStepRoute(to) && to.meta.step.requiresEnterValidation && isStepRoute(from)) {
      const indexFrom = stepsStore.getIndex(from)
      if (indexFrom === -1) return true

      const indexTo = stepsStore.getIndex(to)
      const canContinue = stepsStore.canGoTo(indexTo)

      lifecycleStore.attemptedSubmit = !canContinue

      return canContinue
    }

    return true
  })

  // If user state has a UUID, add it to the url
  // This should only happen when the user navigates back after saving
  router.afterEach((to, from) => {
    const stepsStore = useStepsStore()

    // If next step requires validation, check if it cen be accessed,
    // otherwise redirect to home
    // This should only happen when the page is accessed directly via URL/deep link
    // since otherwise the router will prevent navigation in the `beforeEach` hook
    if (
      !isStepRoute(from) &&
      isStepRoute(to) &&
      to.meta.step.requiresEnterValidation &&
      !stepsStore.canGoTo(stepsStore.getIndex(to))
    ) {
      router.replace('/')
      return
    }

    initMultiInstanceWarning(router)

    const requestMetaStore = useRequestMetaStore()

    if (requestMetaStore.uuid) {
      const url = new URL(window.location.href)
      if (url.searchParams.has('r')) return
      url.searchParams.set('r', requestMetaStore.uuid)
      history.replaceState(history.state, '', url.toString())
    }
  })

  return router
}
